import noop from 'lodash/noop';
import classNames from 'classnames';
import type React from 'react';
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { I18n } from 'common/i18n';
import { Key } from 'common/types/keyboard/key';
import { EMPTY_TRANSPARENT_COLOR } from 'common/authoring_workflow/constants';
import { ColorBuckets } from 'common/components/ColorPicker/ColorBuckets';
import { ForgeIcon, ForgePopover } from '@tylertech/forge-react';

import './index.scss';

/**
 * `null` means no color is selected, which renders an empty swatch. Distinct from a
 * `value` of `undefined`, which falls back to the default color.
 */
type ColorPickerValue = string | null;

export interface ColorPickerProps {
  bucketRevealDirection?: string;
  id: string;
  value?: ColorPickerValue;
  palette?: string[];
  handleColorChange?: (color: string) => void;
  hideColorFrame?: boolean;
}

export interface ColorPickerHandle {
  toggleColorPicker: () => void;
}

/**
 * Uses forwardRef so parents can imperatively open/close the picker via a ref
 * (see ColorPickerHandle). Only BlockSectionEditControls needs this — its
 * background-color button lives outside this component and calls
 * toggleColorPicker() on click. Other call sites render <ColorPicker /> without
 * a ref and let it manage its own open state.
 */
export const ColorPicker = forwardRef<ColorPickerHandle, ColorPickerProps>(function ColorPicker(
  { bucketRevealDirection, id, value = '#204490', palette, handleColorChange = noop, hideColorFrame = false },
  ref,
) {
  const [selectedColor, setSelectedColor] = useState<ColorPickerValue>(value);
  const [showingBuckets, setShowingBuckets] = useState(false);
  const colorFrameRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setSelectedColor(value);
  }, [value]);

  // used by BlockSectionEditControls
  useImperativeHandle(ref, () => ({
    toggleColorPicker: () => setShowingBuckets((showing) => !showing),
  }));

  const onClickBucket = (color: string) => {
    setShowingBuckets(false);
    setSelectedColor(color);

    handleColorChange(color);
    // Remove focus in an iframe - layout narrative || expanded || large
    // to enable(.active) rich media editor right after clicking a color, then reselecting text.
    const activeElement = document.activeElement as HTMLElement | null;
    if (activeElement?.closest('.layout-narrative, .layout-expanded, .layout-large')) {
      activeElement.blur();
    }
    colorFrameRef.current?.focus();
  };

  const onKeyUpColorPicker = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const openKeys = [Key.ArrowDown, Key.Down_MSIE, Key.Enter, Key.Space];
    if (openKeys.includes(event.key as Key)) {
      setShowingBuckets(true);
      event.preventDefault();
      event.stopPropagation();
    } else if (event.key === Key.Escape) {
      setShowingBuckets(false);
      event.preventDefault();
      event.stopPropagation();
    }
  };

  const renderColorFrame = () => {
    if (hideColorFrame) {
      // previously components that didn't use the frame used display: none
      // however, that would make the popover also display: none and be positioned incorrectly
      return <div id={`${id}-color-frame`} ref={colorFrameRef} />;
    }
    const openColorPicker = I18n.t('shared.components.color_picker.open_color_picker');
    const withCurrentSelection = I18n.t('shared.components.color_picker.with_currently_selected_color');
    const label = selectedColor
      ? `${openColorPicker} ${withCurrentSelection} ${selectedColor}`
      : openColorPicker;

    return (
      <div
        id={`${id}-color-frame`}
        className="color-frame"
        role="button"
        tabIndex={0}
        onKeyUp={onKeyUpColorPicker}
        ref={colorFrameRef}
        aria-label={label}
      >
        <div className="selected-color-frame-wrapper">
          <div
            className={classNames('selected-color-frame', {
              'color-none': selectedColor === EMPTY_TRANSPARENT_COLOR,
            })}
            style={{ backgroundColor: selectedColor as string }}
            data-testid="selected-color-frame"
          />
        </div>
        <div className="arrow-down">
          <ForgeIcon name="arrow_drop_down" />
        </div>
      </div>
    );
  };

  const renderColorPickerOverlay = () => {
    const anchorInfo = colorFrameRef.current
      ? { anchorElement: colorFrameRef.current }
      : { anchor: `${id}-color-frame` };

    return (
      <ForgePopover
        {...anchorInfo}
        open={showingBuckets}
        on-forge-popover-toggle={(evt: CustomEvent<{ newState: string }>) => {
          setShowingBuckets(evt.detail.newState === 'open');
        }}
        placement="bottom-start"
      >
        <ColorBuckets
          bucketRevealDirection={bucketRevealDirection}
          palette={palette}
          selectedColor={selectedColor}
          showingBuckets={showingBuckets}
          onClickBucket={onClickBucket}
        />
      </ForgePopover>
    );
  };

  return (
    <div className="color-picker" id={id}>
      {renderColorPickerOverlay()}
      {renderColorFrame()}
    </div>
  );
});
